/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import {
  CheckAuthStatusResponse,
  LoginApiResponse,
  LoginRequest,
  SecurityAnswerApiRequest,
  SecurityAnswerApiResponse,
  ResetPasswordInfoResponse,
  ResetPasswordSecurityQuestionRequest,
  ResetPasswordSecurityQuestionResponse,
  ResetExpiredPasswordRequest,
  ResetExpiredPasswordResponse,
} from '../models/AuthTypes';
import { AppViewModel } from '../models/AppViewModel';
import { createApi } from '@reduxjs/toolkit/query/react';
import { retry } from '@reduxjs/toolkit/query';
import type {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
  RetryOptions,
} from '@reduxjs/toolkit/query';
import { waPlatformQuery, waServicesQuery } from './baseQueries';
import { RecordResponse } from '@/models/ServiceTypes';
import { consumeRequestedRole } from '@/utils/roleSwitch';

type FormValue = string | number | boolean | null | undefined;

type JsonPrimitive = string | number | boolean | null;
type JsonObject = { [key: string]: JsonValue };
type JsonArray = JsonValue[];
type JsonValue = JsonPrimitive | JsonObject | JsonArray;

type AuthPlatformFailureResponse = {
  success: false;
  errors?: string[];
  error?: string;
};

function isJsonObject(value: JsonValue): value is JsonObject {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function isAuthPlatformFailureResponse(
  value: JsonValue,
): value is AuthPlatformFailureResponse {
  if (!isJsonObject(value) || value.success !== false) {
    return false;
  }

  const hasValidErrors =
    value.errors === undefined ||
    (Array.isArray(value.errors) &&
      value.errors.every(
        (entry): entry is string => typeof entry === 'string',
      ));
  const hasValidError =
    value.error === undefined || typeof value.error === 'string';

  return hasValidErrors && hasValidError;
}

/** Retries after the first attempt (3 attempts in total). */
const UNAUTH_PRIVATE_LABEL_MAX_RETRIES = 2;

/** Only network failures, timeouts and 5xx are transient; 4xx and parse errors are not. */
// `unknown` is required: RTK types the error handed to `retryCondition` as `unknown`.
function isTransientLabelError(error: unknown): boolean {
  if (typeof error !== 'object' || error === null || !('status' in error)) {
    return false;
  }
  const { status } = error;
  return (
    status === 'FETCH_ERROR' ||
    status === 'TIMEOUT_ERROR' ||
    (typeof status === 'number' && status >= 500)
  );
}

// RTK's default retry would retry every error up to 5 times, so restrict it to transient errors.
const unauthPrivateLabelRetryOptions: RetryOptions = {
  retryCondition: (error, _args, { attempt }) =>
    attempt <= UNAUTH_PRIVATE_LABEL_MAX_RETRIES && isTransientLabelError(error),
};

export type UnauthPrivateLabelResponse = {
  Found?: boolean;
  Host?: string;
  AdvisorGroupId: number | null;
  Enabled?: boolean;
  CustomUrl?: string | null;
  AboutUrl: string | null;
  ImagePath: string | null;
  Url: string | null;
  Name: string | null;
  IconPath: string | null;
  ThemeFile: string | null;
  LogoHeight?: number | null;
  MobileLogoPath: string | null;
  PrimaryColor?: string | null;
  PrimaryColorHex?: string | null;
  SecondaryColor?: string | null;
  SecondaryColorHex?: string | null;
  MobileImagePath?: string | null;
};

// `unknown` is required: response.json() and waServicesQuery return untyped data; the guard is shallow by design (callers check logo paths).
function isUnauthPrivateLabelBody(
  value: unknown,
): value is UnauthPrivateLabelResponse | null {
  return value === null || (typeof value === 'object' && !Array.isArray(value));
}

const unauthPrivateLabelParseError: FetchBaseQueryError = {
  status: 'PARSING_ERROR',
  originalStatus: 200,
  data: '',
  error: 'UnauthPrivateLabel response was neither null nor an object',
};

async function readUnauthPrivateLabelBody(
  response: Response,
): Promise<
  | { data: UnauthPrivateLabelResponse | null; error?: undefined }
  | { error: FetchBaseQueryError }
> {
  return response
    .json()
    .then((body: unknown) =>
      isUnauthPrivateLabelBody(body)
        ? { data: body }
        : { error: unauthPrivateLabelParseError },
    )
    .catch(() => ({ error: unauthPrivateLabelParseError }));
}

function jsonToFormDataString(json: Record<string, FormValue>): string {
  const params = new URLSearchParams();
  Object.entries(json).forEach(([key, value]) => {
    if (value === null || value === undefined) {
      return;
    }
    params.set(key, String(value));
  });
  return params.toString();
}

function isAppAccountRedirect(response: Response): boolean {
  try {
    if (!response.redirected || typeof response.url !== 'string') {
      return false;
    }
    return new URL(response.url).pathname.includes('/App/Account');
  } catch {
    return false;
  }
}

// Wrap the shared platform query to preserve custom response handling behavior used by auth endpoints
const authPlatformQuery: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = (args, api, extraOptions) => {
  const request: FetchArgs = typeof args === 'string' ? { url: args } : args;

  return waPlatformQuery(
    {
      ...request,
      responseHandler: async (response) => {
        try {
          if (response.redirected && typeof response.url === 'string') {
            const redirectedUrl = new URL(response.url);
            const getUsernameFromUrl = (
              url: URL,
              options?: { includeHashQuery?: boolean },
            ): string | null => {
              const username =
                url.searchParams.get('userName') ??
                url.searchParams.get('username');
              if (username) {
                return username;
              }
              if (options?.includeHashQuery && url.hash.includes('?')) {
                const hashQuery = url.hash.split('?')[1] ?? '';
                const hashParams = new URLSearchParams(hashQuery);
                return hashParams.get('userName') ?? hashParams.get('username');
              }
              return null;
            };

            const getUsernameFromRequestBody = (): string | null => {
              // loginUser sends x-www-form-urlencoded string via jsonToFormDataString.
              // Use it as a fallback so lockout handling doesn't depend on URL fragments.
              if (typeof request.body !== 'string') {
                return null;
              }
              const params = new URLSearchParams(request.body);
              return (
                params.get('UserName') ??
                params.get('userName') ??
                params.get('username')
              );
            };

            if (
              redirectedUrl.pathname.includes(
                '/Account/MultiFactorAuthenticationInit',
              )
            ) {
              const username = getUsernameFromUrl(redirectedUrl);
              return {
                success: false,
                action: 'LoginVerification',
                username,
                forceTwoFactor: true,
              };
            }

            // Account lockout redirects to the legacy route:
            //   /App/Account#lockedout?username=...
            // Fetch follows redirects, but fragments (#...) may be dropped. Treat any redirect
            // from POST /Account/Login → /App/Account as lockout and use request body to
            // recover the username.
            const url = request.url;
            const method = (request.method ?? 'GET').toUpperCase();
            const isLoginPost =
              url.includes('/Account/Login') && method === 'POST';

            if (
              isLoginPost &&
              redirectedUrl.pathname.includes('/App/Account')
            ) {
              const username =
                getUsernameFromUrl(redirectedUrl, { includeHashQuery: true }) ??
                getUsernameFromRequestBody();

              return {
                success: false,
                action: 'Lockout',
                username,
              };
            }
          }
        } catch {
          // ignore and fall through to JSON handling
        }

        const data = (await response.json()) as JsonValue;
        const url = request.url;
        const method = (request.method ?? 'GET').toUpperCase();
        const isLoginPost = url.includes('/Account/Login') && method === 'POST';

        if (!isLoginPost && isAuthPlatformFailureResponse(data)) {
          if (Array.isArray(data.errors) && data.errors.length > 0) {
            throw new Error(data.errors.join(', '));
          }
          throw new Error(data.error ?? 'Request failed');
        }
        return data;
      },
    },
    api,
    extraOptions,
  );
};

const authApi = createApi({
  baseQuery: authPlatformQuery,
  tagTypes: ['ResetPasswordInfo'],
  endpoints: (builder) => ({
    loginUser: builder.mutation<LoginApiResponse, LoginRequest>({
      query: (data) => ({
        url: '/Account/Login',
        method: 'POST',
        headers: {
          'content-type': 'application/x-www-form-urlencoded',
          // Platform rejects login POSTs without this header and may return an HTML error page,
          // which then causes `response.json()` to throw `Unexpected token '<'`.
          'x-requested-with': 'XMLHttpRequest',
          accept: 'application/json',
        },
        body: jsonToFormDataString(data),
        validateStatus: (response) =>
          response.status < 400 || isAppAccountRedirect(response),
      }),
    }),
    contactSupport: builder.mutation<
      unknown,
      {
        Description: string;
        currentRole: string;
        userName: string;
        isImpersonation: boolean;
        requestorController: string;
        type: string;
      }
    >({
      async queryFn(arg, api, extraOptions, _baseQuery) {
        const result = await waServicesQuery(
          {
            url: '/api/user/ContactSupport',
            method: 'POST',
            headers: { 'content-type': 'application/json; charset=UTF-8' },
            body: arg,
          },
          api,
          extraOptions,
        );
        if (result.error) {
          return { error: result.error };
        }
        return { data: result.data };
      },
    }),
    // eslint-disable-next-line @typescript-eslint/no-invalid-void-type -- RTK Query uses void for no-argument endpoints
    getContactSupportTypes: builder.query<string[], void>({
      async queryFn(_arg, api, extraOptions, _baseQuery) {
        const result = await waServicesQuery(
          {
            url: '/api/user/contactSupportTypes',
            method: 'GET',
          },
          api,
          extraOptions,
        );

        if (result.error) {
          return { error: result.error };
        }

        const data = result.data as RecordResponse<string>;
        return { data: data.Records };
      },
    }),
    getUnauthPrivateLabel: builder.query<
      UnauthPrivateLabelResponse | null,
      // eslint-disable-next-line @typescript-eslint/no-invalid-void-type -- RTK Query uses void for no-argument endpoints
      void
    >({
      queryFn: retry(async (_arg, api, extraOptions) => {
        if (import.meta.env.DEV && typeof window !== 'undefined') {
          try {
            const response = await fetch('/api/account/UnauthPrivateLabel', {
              method: 'GET',
              headers: { accept: 'application/json' },
            });

            if (!response.ok) {
              const bodyText = await response.text().catch(() => '');
              const httpError: FetchBaseQueryError = {
                status: response.status,
                data: bodyText,
              };
              return { error: httpError };
            }

            return await readUnauthPrivateLabelBody(response);
          } catch (e) {
            const fetchError: FetchBaseQueryError = {
              status: 'FETCH_ERROR',
              error: e instanceof Error ? e.message : 'Fetch error',
            };
            return { error: fetchError };
          }
        }

        const result = await waServicesQuery(
          {
            url: '/api/account/UnauthPrivateLabel',
            method: 'GET',
          },
          api,
          extraOptions,
        );

        if (result.error) {
          return { error: result.error };
        }

        if (!isUnauthPrivateLabelBody(result.data)) {
          return { error: unauthPrivateLabelParseError };
        }
        return { data: result.data };
      }, unauthPrivateLabelRetryOptions),
    }),
    getUnauthPrivateLabelByAdvisorGroupId: builder.query<
      UnauthPrivateLabelResponse | null,
      { advisorGroupId: number }
    >({
      queryFn: retry(
        async (
          { advisorGroupId }: { advisorGroupId: number },
          api,
          extraOptions,
        ) => {
          try {
            // DEV: use relative /api so Vite proxy can spoof origin if configured.
            if (import.meta.env.DEV && typeof window !== 'undefined') {
              const url = `/api/account/UnauthPrivateLabel?advisorGroupId=${encodeURIComponent(
                String(advisorGroupId),
              )}`;
              const resp = await fetch(url, {
                method: 'GET',
                headers: { accept: 'application/json' },
              });
              if (!resp.ok) {
                const bodyText = await resp.text().catch(() => '');
                console.warn(
                  'UnauthPrivateLabel by advisorGroupId request failed (DEV)',
                  resp.status,
                  url,
                  bodyText,
                );
                const httpError: FetchBaseQueryError = {
                  status: resp.status,
                  data: bodyText,
                };
                return { error: httpError };
              }
              return await readUnauthPrivateLabelBody(resp);
            }

            // non-DEV: call services directly with query param.
            const result = await waServicesQuery(
              {
                url: '/api/account/UnauthPrivateLabel',
                method: 'GET',
                params: { advisorGroupId },
              },
              api,
              extraOptions,
            );

            if (result.error) {
              return { error: result.error };
            }

            if (!isUnauthPrivateLabelBody(result.data)) {
              return { error: unauthPrivateLabelParseError };
            }
            return { data: result.data };
          } catch (e) {
            const fetchError: FetchBaseQueryError = {
              status: 'FETCH_ERROR',
              error: e instanceof Error ? e.message : 'Fetch error',
            };
            return { error: fetchError };
          }
        },
        unauthPrivateLabelRetryOptions,
      ),
    }),
    verifyResetPasswordPin: builder.mutation<
      boolean,
      { username: string; PIN: string }
    >({
      async queryFn(arg, api, extraOptions, _baseQuery) {
        const result = await waServicesQuery(
          {
            url: '/api/account/resetPasswordInfo/verifyByPin',
            method: 'PUT',
            headers: { 'content-type': 'application/json; charset=UTF-8' },
            body: { username: arg.username, PIN: arg.PIN },
          },
          api,
          extraOptions,
        );
        if (result.error) {
          return { error: result.error };
        }
        const isCorrect = result.data as boolean;
        if (!isCorrect) {
          const customError: FetchBaseQueryError = {
            status: 400,
            data: { message: 'Incorrect PIN' },
          };
          return { error: customError };
        }
        return { data: isCorrect };
      },
      invalidatesTags: (_res, _err, arg) => [
        { type: 'ResetPasswordInfo', id: arg.username },
      ],
    }),
    verifyResetPasswordCode: builder.mutation<
      boolean,
      { username: string; Code: string }
    >({
      async queryFn(arg, api, extraOptions, _baseQuery) {
        const result = await waServicesQuery(
          {
            url: '/api/account/resetPasswordInfo/verifyByCode',
            method: 'PUT',
            headers: { 'content-type': 'application/json; charset=UTF-8' },
            body: { username: arg.username, Code: arg.Code },
          },
          api,
          extraOptions,
        );
        if (result.error) {
          return { error: result.error };
        }
        const isCorrect = result.data as boolean;
        if (!isCorrect) {
          const customError: FetchBaseQueryError = {
            status: 400,
            data: { message: 'Incorrect code' },
          };
          return { error: customError };
        }
        return { data: isCorrect };
      },
      invalidatesTags: (_res, _err, arg) => [
        { type: 'ResetPasswordInfo', id: arg.username },
      ],
    }),
    logout: builder.mutation({
      query: () => ({
        url: '/Account/LogOff',
        method: 'POST',
        headers: {
          'content-type': 'application/x-www-form-urlencoded',
          'x-requested-with': 'XMLHttpRequest',
          accept: 'application/json',
        },
        body: jsonToFormDataString({ useJson: true }),
        credentials: 'include',
        redirect: 'manual',
        validateStatus: (response) => response.status < 400,
      }),
    }),
    submitSecurityAnswer: builder.mutation<
      SecurityAnswerApiResponse,
      SecurityAnswerApiRequest
    >({
      async queryFn(arg, _api, _extra, _baseQuery) {
        const result = await waPlatformQuery(
          {
            url: '/Account/LoginVerification',
            method: 'POST',
            headers: {
              'content-type': 'application/x-www-form-urlencoded',
              'x-requested-with': 'XMLHttpRequest',
              accept: 'application/json',
            },
            body: jsonToFormDataString(arg),
            credentials: 'include',
          },
          _api,
          _extra,
        );
        if (result.error) {
          return { error: result.error };
        }
        return { data: result.data as SecurityAnswerApiResponse };
      },
    }),
    loginSendSecurityCode: builder.mutation<unknown, { username: string }>({
      query: ({ username }) => ({
        url: '/Account/LoginSendSecurityCode',
        method: 'POST',
        headers: {
          'content-type': 'application/x-www-form-urlencoded',
          'x-requested-with': 'XMLHttpRequest',
          accept: 'application/json',
        },
        body: jsonToFormDataString({ username }),
        credentials: 'include',
        validateStatus: (response) => response.status < 400,
      }),
    }),
    loginSendVoiceSecurityCode: builder.mutation<unknown, { username: string }>(
      {
        query: ({ username }) => ({
          url: '/Account/LoginSendVoiceSecurityCode',
          method: 'POST',
          headers: {
            'content-type': 'application/x-www-form-urlencoded',
            'x-requested-with': 'XMLHttpRequest',
            accept: 'application/json',
          },
          body: jsonToFormDataString({ username }),
          credentials: 'include',
          validateStatus: (response) => response.status < 400,
        }),
      },
    ),
    loginVerificationCode: builder.mutation<
      unknown,
      { username: string; code: string; rememberMe: boolean }
    >({
      query: ({ username, code, rememberMe }) => ({
        url: '/Account/LoginVerificationCode',
        method: 'POST',
        headers: {
          'content-type': 'application/x-www-form-urlencoded',
          'x-requested-with': 'XMLHttpRequest',
          accept: 'application/json',
        },
        body: jsonToFormDataString({ username, code, rememberMe }),
        credentials: 'include',
        validateStatus: (response) => response.status < 400,
      }),
    }),
    getResetPasswordInfo: builder.query<ResetPasswordInfoResponse, string>({
      async queryFn(username, api, extraOptions, _baseQuery) {
        const result = await waServicesQuery(
          {
            url: '/api/account/resetPasswordInfo',
            method: 'GET',
            params: { username },
          },
          api,
          extraOptions,
        );
        if (result.error) {
          return { error: result.error };
        }
        return { data: result.data as ResetPasswordInfoResponse };
      },
      providesTags: (_res, _err, username) => [
        { type: 'ResetPasswordInfo', id: username },
      ],
    }),
    getResetPasswordQuestion: builder.query<
      ResetPasswordSecurityQuestionResponse,
      string
    >({
      async queryFn(username, api, extraOptions, _baseQuery) {
        const result = await waServicesQuery(
          {
            url: '/api/account/securityQuestion',
            method: 'GET',
            params: { username },
          },
          api,
          extraOptions,
        );
        if (result.error) {
          return { error: result.error };
        }
        return { data: result.data as ResetPasswordSecurityQuestionResponse };
      },
      async onQueryStarted(username, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch {
          // If fetching the question errors (e.g., user locked out), ensure
          // the ResetPasswordInfo query is refreshed so parent can render lockout.
          dispatch(
            authApi.util.invalidateTags([
              { type: 'ResetPasswordInfo', id: username },
            ]),
          );
        }
      },
    }),
    sendResetPasswordSMSCode: builder.mutation<unknown, { username: string }>({
      async queryFn(arg, api, extraOptions, _baseQuery) {
        const result = await waServicesQuery(
          {
            url: '/api/account/resetPasswordInfo/sendSMSCode',
            method: 'PUT',
            headers: { 'content-type': 'application/json; charset=UTF-8' },
            body: { username: arg.username },
          },
          api,
          extraOptions,
        );
        if (result.error) {
          return { error: result.error };
        }
        return { data: result.data };
      },
    }),
    sendResetPasswordVoiceCode: builder.mutation<unknown, { username: string }>(
      {
        async queryFn(arg, api, extraOptions, _baseQuery) {
          const result = await waServicesQuery(
            {
              url: '/api/account/resetPasswordInfo/sendVoiceCode',
              method: 'PUT',
              headers: { 'content-type': 'application/json; charset=UTF-8' },
              body: { username: arg.username },
            },
            api,
            extraOptions,
          );
          if (result.error) {
            return { error: result.error };
          }
          return { data: result.data };
        },
      },
    ),
    submitResetPasswordAnswer: builder.mutation<
      boolean,
      ResetPasswordSecurityQuestionRequest
    >({
      async queryFn(data, api, extraOptions, _baseQuery) {
        const result = await waServicesQuery(
          {
            url: '/api/account/securityQuestion/',
            method: 'PUT',
            body: data,
          },
          api,
          extraOptions,
        );
        if (result.error) {
          return { error: result.error };
        }
        const isCorrect = result.data as boolean;
        if (!isCorrect) {
          const customError: FetchBaseQueryError = {
            status: 400,
            data: { message: 'Incorrect answer' },
          };
          return { error: customError };
        }
        return { data: isCorrect };
      },
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch {
          // Swallow error to avoid unhandled promise rejection in console.
        } finally {
          dispatch(
            authApi.util.invalidateTags([
              { type: 'ResetPasswordInfo', id: arg.Username },
            ]),
          );
        }
      },
    }),
    // eslint-disable-next-line @typescript-eslint/no-invalid-void-type -- RTK Query uses void for no-argument endpoints
    checkAuthStatus: builder.query<CheckAuthStatusResponse, void>({
      query: () => ({
        url: '/Account/Login',
        method: 'GET',
        headers: {
          'content-type': 'application/x-www-form-urlencoded',
        },
        credentials: 'include',
        params: { useJson: true, requestedRole: consumeRequestedRole() },

        validateStatus: (response) => response.status < 400,
      }),
    }),
    accessInvestor: builder.mutation<
      AppViewModel,
      { investorId: number; role?: string }
    >({
      query: ({ investorId, role }) => ({
        url: '/App/AccessInvestor',
        method: 'GET',
        params: {
          investorId,
          ...(role ? { role } : {}),
          useJson: true,
        },
        credentials: 'include',
      }),
    }),
    accessAdvisor: builder.mutation<
      AppViewModel,
      { advisorId: number; role?: string }
    >({
      query: ({ advisorId, role }) => ({
        url: '/App/AccessAdvisor',
        method: 'GET',
        params: {
          advisorId,
          useJson: true,
          ...(role ? { role } : {}),
        },
        credentials: 'include',
      }),
    }),
    accessAdvisorForTeamMember: builder.mutation<
      AppViewModel,
      { advisorId: number; role?: string }
    >({
      query: ({ advisorId, role }) => ({
        url: '/App/AccessAdvisorForTeamMember',
        method: 'GET',
        params: {
          advisorId,
          useJson: true,
          ...(role ? { role } : {}),
        },
        credentials: 'include',
      }),
    }),
    resetExpiredPassword: builder.mutation<
      ResetExpiredPasswordResponse,
      ResetExpiredPasswordRequest
    >({
      async queryFn(arg, api, extraOptions, _baseQuery) {
        const result = await waPlatformQuery(
          {
            url: '/Account/ConfirmPasswordReset',
            method: 'POST',
            headers: { 'content-type': 'application/x-www-form-urlencoded' },
            body: jsonToFormDataString(arg),
            credentials: 'include',
            // responseHandler: "text",
          },
          api,
          extraOptions,
        );
        if (result.error) {
          return { error: result.error };
        }
        return { data: result.data as ResetExpiredPasswordResponse };
      },
    }),
    // eslint-disable-next-line @typescript-eslint/no-invalid-void-type -- RTK Query uses void for no-argument endpoints
    resetSessionVariables: builder.mutation<unknown, void>({
      async queryFn(_arg, _context, _extra, baseQuery) {
        const headers: Record<string, string> = {};

        const result = await baseQuery({
          url: '/App/ResetSessionVariables',
          method: 'GET',
          params: { _: Date.now() },
          credentials: 'include',
          headers: Object.keys(headers).length ? headers : undefined,
        });

        if (result.error) {
          return { error: result.error };
        }

        return { data: result.data };
      },
    }),
  }),
});

export const {
  useLoginUserMutation,
  useGetUnauthPrivateLabelQuery,
  useGetUnauthPrivateLabelByAdvisorGroupIdQuery,
  useSubmitSecurityAnswerMutation,
  useLoginSendSecurityCodeMutation,
  useLoginSendVoiceSecurityCodeMutation,
  useLoginVerificationCodeMutation,
  useLogoutMutation,
  useCheckAuthStatusQuery,
  useLazyCheckAuthStatusQuery,
  useGetResetPasswordInfoQuery,
  useGetResetPasswordQuestionQuery,
  useSubmitResetPasswordAnswerMutation,
  useAccessInvestorMutation,
  useAccessAdvisorMutation,
  useAccessAdvisorForTeamMemberMutation,
  useResetExpiredPasswordMutation,
  useResetSessionVariablesMutation,
  useSendResetPasswordSMSCodeMutation,
  useSendResetPasswordVoiceCodeMutation,
  useVerifyResetPasswordPinMutation,
  useVerifyResetPasswordCodeMutation,
  useContactSupportMutation,
  useGetContactSupportTypesQuery,
} = authApi;
export default authApi;
